<template>
  <div class="login-password-page">
    <div class="login-password-header">
      <button
        @click="$router.push({ name: 'login' })"
        class="btn btn-secondary btn-back"
      >
        <i class="bi bi-arrow-left"></i>
        <span>Retour</span>
      </button>
    </div>

    <div class="login-password-content">
      <div class="login-form">
        <h2 class="form-title">Connexion</h2>

        <div class="form-group">
          <label for="reference" class="form-label"
            >Référence utilisateur</label
          >
          <input
            id="reference"
            type="text"
            class="form-input"
            :value="reference"
            disabled
            readonly
          />
        </div>

        <div class="form-group">
          <label for="password" class="form-label">Mot de passe</label>
          <div class="password-input-wrapper">
            <input
              id="password"
              :type="showPassword ? 'text' : 'password'"
              v-model="password"
              class="form-input"
              placeholder="Entrez votre mot de passe"
              @keyup.enter="handleLogin"
              :disabled="isLoggingIn"
            />
            <button
              type="button"
              class="btn btn-toggle-password"
              @click="showPassword = !showPassword"
              tabindex="-1"
            >
              <i v-if="showPassword" class="bi bi-eye-slash-fill"></i>
              <i v-else class="bi bi-eye-fill"></i>
            </button>
          </div>
        </div>

        <button
          type="button"
          @click="handleLogin"
          class="btn btn-primary btn-login"
          :disabled="isLoggingIn || !password"
        >
          <span v-if="isLoggingIn">Connexion en cours...</span>
          <span v-else>Se connecter</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      password: "",
      showPassword: false,
      isLoggingIn: false,
    };
  },
  computed: {
    reference() {
      return this.$route.params.reference || "";
    },
  },
  methods: {
    async handleLogin() {
      // Prevent multiple simultaneous login requests
      if (this.isLoggingIn) {
        return;
      }

      // Validate inputs
      if (!this.password || !this.reference) {
        if (this.$toast) {
          this.$toast.error("Veuillez remplir tous les champs.");
        }
        return;
      }

      // Attempt login with error handling
      try {
        this.isLoggingIn = true;
        await this.$store.dispatch("auth/login", {
          username: this.reference,
          phone: this.reference,
          password: this.password,
          reference: this.reference,
        });
        // If login succeeds, router will navigate away automatically
        // No need to reset isLoggingIn as component will be destroyed
      } catch (error) {
        console.error("Login error in LoginPassword component:", error);
        // Reset flag on error so user can try again
        this.isLoggingIn = false;

        // Show error message to user if toast is available
        if (this.$toast) {
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            "Mot de passe incorrect. Veuillez réessayer.";
          this.$toast.error(errorMessage);
        }

        // Clear password on error so user can retry
        this.password = "";
      }
    },
  },
  mounted() {
    if (!this.reference) {
      this.$router.push({ name: "login" });
    }
  },
};
</script>

<style scoped>
.login-password-page {
  min-height: 100vh;
  background: var(--bg-secondary);
  padding: 2rem;
  display: flex;
  flex-direction: column;
}

.login-password-header {
  max-width: 600px;
  margin: 0 auto 2rem;
  width: 100%;
}

.btn-back {
  padding: 0.75rem 1.5rem;
}

.login-password-content {
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
}

.login-form {
  background: var(--bg-card);
  border-radius: var(--radius-lg);
  padding: 2.5rem;
  box-shadow: var(--shadow-lg);
}

.form-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 2rem;
  text-align: center;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
}

.form-input {
  width: 100%;
  padding: 0.875rem 1rem;
  font-size: 1rem;
  border: 2px solid var(--border-color);
  border-radius: var(--radius-md);
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: var(--transition);
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb), 0.1);
}

.form-input:disabled {
  background: var(--bg-secondary);
  color: var(--text-secondary);
  cursor: not-allowed;
  opacity: 0.7;
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-wrapper .form-input {
  padding-right: 3rem;
}

.btn-toggle-password {
  position: absolute;
  right: 0.75rem;
  background: transparent;
  border: none;
  color: var(--text-secondary);
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition);
}

.btn-toggle-password:hover {
  color: var(--primary-color);
}

.btn-toggle-password i {
  font-size: 1.25rem;
}

.btn-login {
  width: 100%;
  padding: 1rem;
  font-size: 1.125rem;
  font-weight: 600;
  margin-top: 1rem;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: var(--transition);
}

.btn-login:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media screen and (max-width: 768px) {
  .login-password-page {
    padding: 1rem;
  }

  .login-form {
    padding: 1.5rem;
  }

  .form-title {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
}
</style>
